import { useRef, useState } from "react";
import { formatDay } from "@oshimap/utils";
import { normalizeCategoryCode } from "@oshimap/domain";
import { CATEGORIES } from "@oshimap/config";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { Button, CategoryChip, VerificationBadge, Skeleton, ErrorState, CategoryIcon, SourceLink } from "../../components/ui";
import { play, haptic, sparkle } from "../../lib/motion";
import { VisitSheet } from "../visit/VisitSheet";
import { AuthSheet } from "../auth/AuthSheet";
import { SubmitSheet } from "../submit/SubmitSheet";
import { LiveSection } from "../live/LiveSection";
import { useAuthPrompt } from "../auth/useAuthPrompt";
import { session } from "../../lib/api";
import "./place-detail.css";
import { openDirections } from "../../lib/directions";
import { ApiError } from "@oshimap/api-client";
import { queryKeys } from "@oshimap/query";

interface Props {
  placeId: string;
  isFavorited: boolean;
  onToggleFavorite: () => void | Promise<unknown>;
  onBack: () => void;
}

export function PlaceDetail({ placeId, isFavorited, onToggleFavorite, onBack }: Props) {
  const favRef = useRef<HTMLButtonElement>(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [directions, setDirections] = useState<string | null>(null);
  const auth = useAuthPrompt();
  const { state, reload } = useQuery(queryKeys.place(placeId), (signal) => ep.getPlace(placeId, { signal }));

  if (state.status === "loading" || state.status === "idle") {
    return <div className="om-detail"><h1 className="sr-only">장소 상세</h1><Header onBack={onBack} /><div className="om-detail__pad"><Skeleton lines={3} /></div></div>;
  }
  if (state.status === "error") {
    return (
      <div className="om-detail"><h1 className="sr-only">장소 상세</h1><Header onBack={onBack} />
        <div className="om-detail__pad">
          {/*
            * 404 는 다시 눌러도 없다. "다시 시도" 를 권하면 사람을 헛되이 붙잡는다 —
            * 그럴 땐 돌아갈 길을 준다. 연결 문제일 때만 재시도가 뜻이 있다.
            */}
          {state.error instanceof ApiError && state.error.status === 404
            ? <ErrorState title="장소를 찾을 수 없어요"
                body="지워졌거나 다른 곳으로 합쳐졌을 수 있어요."
                action={<Button variant="primary" size="sm" onClick={onBack}>뒤로</Button>} />
            : <ErrorState title="장소를 불러오지 못했어요" body={state.error.message} onRetry={reload} />}
        </div>
      </div>
    );
  }

  const p = state.data;
  const cat = normalizeCategoryCode(p.category.code);
  return (
    <div className="om-detail">
      <div className="om-detail__hero">
        <span className="om-detail__glyph"><CategoryIcon code={cat} size={60} /></span>
        <button className="om-detail__back" onClick={onBack} aria-label="뒤로">‹</button>
        <button
          ref={favRef} className="om-detail__fav" aria-pressed={isFavorited}
          aria-label={isFavorited ? "저장 해제" : "저장"}
          onClick={() => auth.require("저장하려면 로그인이 필요해요", async () => {
            play(favRef.current, "mo-pop"); haptic("POP");
            await onToggleFavorite();
            if (!isFavorited) sparkle(favRef.current, 5);
          })}
        >{isFavorited ? "♥" : "♡"}</button>
      </div>

      <div className="om-detail__pad">
        <div className="om-detail__meta">
          <CategoryChip code={p.category.code} />
          <VerificationBadge status={p.verificationStatus} lastVerifiedAt={p.lastVerifiedAt} />
        </div>
        <h1 className="om-detail__title">{p.name}</h1>
        <p className="om-detail__addr">{p.location.addressLine}</p>
        {p.description ? <p className="om-detail__desc">{p.description}</p> : null}

        {directions ? <p className="om-note" role="status">{directions}</p> : null}
        <div className="om-detail__actions">
          <Button variant="primary" style={{ flex: 1 }}
            onClick={() => { void openDirections(p.location, p.name, p.location.addressLine).then((r) => {
              setDirections(r === "copied" ? "주소를 복사했어요." : r === "failed" ? "주소를 복사하지 못했어요." : null);
            }).catch(() => setDirections("길찾기를 열지 못했어요.")); }}>길찾기</Button>
          <Button variant="secondary" style={{ flex: 1 }}
            onClick={() => auth.require("방문을 기록하려면 로그인이 필요해요", () => setVisitOpen(true))}>
            방문 기록
          </Button>
        </div>

        {/*
          * 현장 정보를 출처보다 위에 둔다. 가기 전에 가장 먼저 알아야 하는 것은
          * "지금 어떤가" 이지 "누가 올렸나" 가 아니다.
          */}
        <LiveSection placeId={p.id} />

        {p.activeEventCount > 0 ? (
          <section className="om-detail__section">
            <h2 className="om-detail__h2">진행 중인 이벤트 <span className="num">{p.activeEventCount}</span></h2>
          </section>
        ) : null}

        {/* P2: 출처는 항상 노출한다. 원문을 복제하지 않고 링크만 보관한다. */}
        <section className="om-detail__section">
          <h2 className="om-detail__h2">출처</h2>
          {p.sourceLinks.length === 0 ? (
            <p className="om-detail__note">아직 등록된 출처가 없습니다. 알고 계신 링크를 제보해 주세요.</p>
          ) : (
            <ul className="om-detail__sources">
              {p.sourceLinks.map((s, i) => (
                <li key={s.id ?? i}>
                  <SourceLink url={s.url} label={s.label} isOfficial={s.isOfficial} />
                </li>
              ))}
            </ul>
          )}
          <p className="om-detail__note">
            최근 확인 <b className="num">{formatDay(p.lastVerifiedAt)}</b>
          </p>
        </section>

        {/* OD-028: 제보는 탭이 아니라 맥락 안의 액션이다 */}
        <Button variant="ghost" block style={{ marginTop: "var(--s-5)" }}
          onClick={() => auth.require("제보하려면 로그인이 필요해요", () => setSubmitOpen(true))}>
          정보가 다른가요? 수정 제보
        </Button>
      </div>

      {visitOpen ? (
        <VisitSheet placeId={p.id} placeName={p.name} onClose={() => setVisitOpen(false)} />
      ) : null}
      {submitOpen ? (
        <SubmitSheet targetPlaceId={p.id} targetName={p.name} onClose={() => setSubmitOpen(false)} />
      ) : null}
      {auth.reason ? <AuthSheet reason={auth.reason} onClose={auth.close} /> : null}
    </div>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="om-detail__hero om-detail__hero--plain">
      <button className="om-detail__back" onClick={onBack} aria-label="뒤로">‹</button>
    </div>
  );
}
